import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import SelectBox from '../SelectBox';
import './KitPopup.css';
import MarketplacesField, { ALL_MARKETPLACES } from '../sets/MarketplacesField';
import AlternateIdsField from '../sets/AlternateIdsField';
import ProductDetailsField from '../sets/ProductDetailsField';

const COMPONENT_TYPES = [
    { value: 'silicagel',        label: 'Desiccant bag',             table: 'silicagelinventory',         displayField: 'brandID',          updateField: 'brandID' },
    { value: 'fert_raw',         label: 'Fert Raw Material',         table: 'fert_raw_materials',         displayField: 'materialName',     updateField: 'id'      },
    { value: 'fert_product',     label: 'Fertilizer',                table: 'fert_products',              displayField: 'productName',      updateField: 'SKU'     },
    { value: 'individualpacket', label: 'Individual Packet',         table: 'individualpackets',          displayField: 'packName',         updateField: 'SKU',     displayPrefix: 'brandID' },
    { value: 'kit',              label: 'Kit',                       table: 'kits',                       displayField: 'name',             updateField: 'SKU'     },
    { value: 'masterbag',        label: 'Master Bag',                table: 'sets',                       displayField: 'setmane',          updateField: 'SKU',     displayPrefix: 'brandID' },
    { value: 'minitools',        label: 'Mini Tools',                table: 'minitoolsinventory',         displayField: 'brandID',          updateField: 'brandID' },
    { value: 'seeds',            label: 'Seeds',                     table: 'seeds',                      displayField: 'combinedSeedsName',updateField: 'seedsID' },
    { value: 'set',              label: 'Set',                       table: 'sets',                       displayField: 'setmane',          updateField: 'SKU',     displayPrefix: 'brandID' },
    { value: 'smallbag',         label: 'Small Bag',                 table: 'individualpackets',          displayField: 'packName',         updateField: 'SKU',     displayPrefix: 'brandID' },
    { value: 'wildmicrmasterbag',label: 'WF/Micro Master Bag',       table: 'wildmicr',                   displayField: 'packName',         updateField: 'SKU',     displayPrefix: 'brandID' },
    { value: 'wildmicr',         label: 'Wildflowers & Microgreens', table: 'wildmicr',                   displayField: 'packName',         updateField: 'SKU',     displayPrefix: 'brandID' },
];

const emptyComponent = () => ({
    _key: Math.random(),
    component_type: '',
    component_ref: '',
    quantity: 1,
    unit: '',
});

const KitPopup = ({ isOpen, onClose, onSaved, editData = null }) => {
    const [kit, setKit] = useState({ SKU: '', name: '', ASIN: '', FNSKU: '', GTIN: '', brandID: '', marketplaces: ALL_MARKETPLACES });
    const [components, setComponents] = useState([emptyComponent()]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        if (editData) {
            setKit({
                SKU:     editData.SKU     || '',
                name:    editData.name    || '',
                ASIN:    editData.ASIN    || '',
                FNSKU:   editData.FNSKU   || '',
                GTIN:    editData.GTIN    || '',
                brandID: editData.brandID || '',
                marketplaces: editData.marketplaces != null ? editData.marketplaces : ALL_MARKETPLACES,
            });
            api.get(`/api/kits/${editData.SKU}/components`)
                .then(r => {
                    const items = r.data.length
                        ? r.data.map(c => ({ ...c, _key: Math.random() }))
                        : [emptyComponent()];
                    setComponents(items);
                })
                .catch(() => setComponents([emptyComponent()]));
        } else {
            setKit({ SKU: '', name: '', ASIN: '', FNSKU: '', GTIN: '', brandID: '', price: '', marketplaces: ALL_MARKETPLACES });
            setComponents([emptyComponent()]);
        }
        setError('');
    }, [isOpen, editData]);

    if (!isOpen) return null;

    const setKitField = (field) => (e) => setKit(p => ({ ...p, [field]: e.target.value }));

    const addComponent = () => setComponents(prev => [...prev, emptyComponent()]);

    const updateComponent = (index, field, value) => {
        setComponents(prev => prev.map((c, i) => {
            if (i !== index) return c;
            const updated = { ...c, [field]: value };
            if (field === 'component_type') updated.component_ref = '';
            return updated;
        }));
    };

    const removeComponent = (index) => {
        setComponents(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!kit.SKU.trim()) { setError('SKU is required'); return; }

        const validComponents = components.filter(c => c.component_type && c.component_ref);

        setSaving(true);
        try {
            const payload = { ...kit, components: validComponents };
            if (editData) {
                await api.put(`/api/kits/${editData.SKU}`, payload);
            } else {
                await api.post(`/api/kits`, payload);
            }
            onSaved();
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const filledCount = components.filter(c => c.component_type && c.component_ref).length;

    return (
        <div className="kit-popup-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="kit-popup">

                {/* ── Header ── */}
                <div className="kit-popup__header">
                    <div>
                        <div className="kit-popup__title">
                            {editData ? `Edit Kit — ${editData.name || editData.SKU}` : 'Add New Kit'}
                        </div>
                        <div className="kit-popup__subtitle">
                            {editData ? 'Update kit details and components' : 'Fill in kit details and add components'}
                        </div>
                    </div>
                    <button className="kit-popup__close" type="button" onClick={onClose} aria-label="Close">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <use xlinkHref="#closeBtn" />
                        </svg>
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="kit-popup__body">

                    {/* Left — kit info */}
                    <div className="kit-popup__info">

                        <div className="kit-field">
                            <span className="kit-field__label">SKU *</span>
                            <input
                                className="kit-field__input"
                                value={kit.SKU}
                                readOnly={!!editData}
                                onChange={setKitField('SKU')}
                                placeholder="e.g. KIT-001"
                            />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Name</span>
                            <input
                                className="kit-field__input"
                                value={kit.name}
                                onChange={setKitField('name')}
                                placeholder="Kit display name"
                            />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">ASIN</span>
                            <input
                                className="kit-field__input"
                                value={kit.ASIN}
                                onChange={setKitField('ASIN')}
                                placeholder="Amazon ASIN"
                            />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">FNSKU</span>
                            <input
                                className="kit-field__input"
                                value={kit.FNSKU}
                                onChange={setKitField('FNSKU')}
                                placeholder="Amazon FNSKU"
                            />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">GTIN</span>
                            <input
                                className="kit-field__input"
                                value={kit.GTIN}
                                onChange={setKitField('GTIN')}
                                placeholder="Barcode / GTIN"
                            />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Brand</span>
                            <SelectBox
                                tableName="brands"
                                displayField="brandName"
                                updateField="brandID"
                                selectedValue={kit.brandID}
                                onChange={val => setKit(p => ({ ...p, brandID: val }))}
                                useFixed
                            />
                        </div>

                        <MarketplacesField
                            value={kit.marketplaces}
                            onChange={v => setKit(p => ({ ...p, marketplaces: v }))}
                        />

                    </div>

                    {/* Right — components */}
                    <div className="kit-popup__components">

                        <div className="kit-comp__toolbar">
                            <span className="kit-comp__heading">
                                Components
                                {filledCount > 0 && (
                                    <span className="kit-comp__count">{filledCount}</span>
                                )}
                            </span>
                            <button type="button" className="kit-comp__add-btn" onClick={addComponent}>
                                + Add Component
                            </button>
                        </div>

                        {components.length > 0 && (
                            <div className="kit-comp__col-headers">
                                <span className="kit-comp__col-header">Type</span>
                                <span className="kit-comp__col-header">Item (SKU / ID)</span>
                                <span className="kit-comp__col-header">Qty</span>
                                <span className="kit-comp__col-header">Unit</span>
                                <span />
                            </div>
                        )}

                        <div className="kit-comp__rows">
                            {components.length === 0 && (
                                <div className="kit-comp__empty">
                                    <span className="kit-comp__empty-icon">📦</span>
                                    <span>No components yet — click "+ Add Component"</span>
                                </div>
                            )}

                            {components.map((comp, idx) => {
                                const typeConfig = COMPONENT_TYPES.find(t => t.value === comp.component_type);
                                return (
                                    <div className="kit-comp__row" key={comp._key ?? idx}>

                                        {/* Type */}
                                        <select
                                            className="kit-comp__select"
                                            value={comp.component_type}
                                            onChange={e => updateComponent(idx, 'component_type', e.target.value)}
                                        >
                                            <option value="">— Type —</option>
                                            {COMPONENT_TYPES.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>

                                        {/* Item selector */}
                                        <div className="kit-comp__item-cell">
                                            {typeConfig ? (
                                                <SelectBox
                                                    key={`${comp._key}-${comp.component_type}`}
                                                    tableName={typeConfig.table}
                                                    displayField={typeConfig.displayField}
                                                    updateField={typeConfig.updateField}
                                                    displayPrefix={typeConfig.displayPrefix}
                                                    selectedValue={comp.component_ref}
                                                    onChange={val => updateComponent(idx, 'component_ref', String(val))}
                                                    useFixed
                                                />
                                            ) : (
                                                <input
                                                    className="kit-comp__input kit-comp__input--disabled"
                                                    placeholder="Select type first"
                                                    disabled
                                                />
                                            )}
                                        </div>

                                        {/* Qty */}
                                        <input
                                            className="kit-comp__input"
                                            type="number"
                                            min="0.001"
                                            step="0.001"
                                            value={comp.quantity}
                                            onChange={e => updateComponent(idx, 'quantity', e.target.value)}
                                        />

                                        {/* Unit */}
                                        <input
                                            className="kit-comp__input"
                                            placeholder="pcs"
                                            value={comp.unit || ''}
                                            onChange={e => updateComponent(idx, 'unit', e.target.value)}
                                        />

                                        {/* Remove */}
                                        <button
                                            type="button"
                                            className="kit-comp__remove"
                                            onClick={() => removeComponent(idx)}
                                            title="Remove"
                                        >
                                            ✕
                                        </button>

                                    </div>
                                );
                            })}
                        </div>

                        {editData && (
                            <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #eef1f4' }}>
                                <ProductDetailsField productType="kits" sku={editData.SKU} />
                                <AlternateIdsField productType="kits" sku={editData.SKU} />
                            </div>
                        )}

                    </div>
                </div>

                {/* ── Footer ── */}
                <div className="kit-popup__footer">
                    <span className="kit-popup__error">
                        {error && `⚠ ${error}`}
                    </span>
                    <button
                        className="kit-popup__submit"
                        onClick={handleSubmit}
                        disabled={saving}
                    >
                        {saving ? 'Saving…' : (editData ? 'Save Changes' : 'Create Kit')}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default KitPopup;
